import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import didClickOnStickyPopover from '@/helper/didClickOnStickyPopover';
import useOnClickOutside from '@/hooks/useOnClickOutside';

import SimpleSankeyLink from './SimpleSankeyLink';
import SimpleSankeyNode from './SimpleSankeyNode';
import type { SankeyLinkWithNodes, SankeyNodeWithLinks, SimpleSankeyDiagramProps } from './types';
import { getWords, measureTextWidth } from './util';

// eslint-disable-next-line lingui/no-unlocalized-strings
const labelFont = 'bold 12px Inter, sans-serif'; // should match the SVG text style

// Helper to get the maximum label width for a set of nodes
function getMaxLabelWidthForNodes(
  nodes: SankeyNodeWithLinks[],
  availableHeight: number,
  maxLevel: number,
): number {
  return nodes.length
    ? Math.max(
        0,
        ...nodes.map((n) => {
          const isOriginating = !n.sourceLink;
          const isTerminating = !n.targetLinks?.length;
          const showValue = isOriginating || isTerminating;
          const { words } = getWords(
            n.name,
            n.value,
            availableHeight,
            showValue,
            n.level === 0 || n.level === maxLevel,
          );
          if (!words.length) return 0;
          return words.reduce((max, word) => Math.max(max, measureTextWidth(word, labelFont)), 0);
        }),
      )
    : 0;
}

const SimpleSankeyDiagram = ({
  description,
  width = 800,
  height = 400,
  minNodeWidth = 80, // minimum width of a node in pixels
  maxNodeWidth = 120, // maximum width of a node in pixels
  minLinkWidth = 16, // minimum horizontal width of a link path in pixels
  minDiagramWidth = 400, // minimum width of the diagram in pixels
  nodeVerticalPadding = 12, // extra space in pixels between nodes
  verticalMargin = 20, // extra space in pixels between nodes and the top/bottom of the diagram
  horizontalMargin = 20, // extra space in pixels between nodes and the left/right of the diagram
  labelPadding = 12, // extra space in pixels between label and node
  minHitboxHeight = 10, // minimum height of transparent node hitbox in pixels
  labelHorizontalPadding = 12, // extra space in pixels between label and node
  nodes,
  links,
}: SimpleSankeyDiagramProps) => {
  const id = useId();
  const [hoveredNode, setHoveredNode] = useState<SankeyNodeWithLinks | undefined>(undefined);
  const [stickyNode, setStickyNode] = useState<
    | {
        element: SVGRectElement;
        node: SankeyNodeWithLinks;
      }
    | undefined
  >(undefined);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const nodeRefs = useRef<Record<string, SVGGElement | null>>({});

  useOnClickOutside(stickyNode?.element, () => setStickyNode(undefined), {
    shouldIgnoreEvent: (event) => didClickOnStickyPopover(event.target),
  });

  const { enhancedNodes, enhancedLinks } = useMemo(() => {
    // Step 1: Create enhanced node objects (no links yet), assign index
    const nodeMap: Record<string, SankeyNodeWithLinks> = {};
    nodes.forEach((n, i) => {
      nodeMap[n.id] = {
        ...n,
        index: i,
        sourceLink: undefined,
        targetLinks: [],
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        value: 0,
      };
    });

    // Step 2: Create enhanced links, referencing enhanced nodes, assign index, and attach to nodes
    const enhancedLinks: SankeyLinkWithNodes[] = links.map((l, i) => {
      const sourceNode = nodeMap[l.sourceId];
      const targetNode = nodeMap[l.targetId];
      const enhancedLink: SankeyLinkWithNodes = {
        value: l.value,
        sourceNode,
        targetNode,
        index: i,
        x0: 0,
        y0: 0,
        x1: 0,
        y1: 0,
        thickness: 0,
      };
      sourceNode.targetLinks!.push(enhancedLink);
      targetNode.sourceLink = enhancedLink;
      return enhancedLink;
    });

    // Step 3: Calculate node values (max of incoming/outgoing)
    const enhancedNodes = Object.values(nodeMap).map((node) => {
      const outgoing = node.targetLinks || [];
      const inSum = node.sourceLink?.value ?? 0;
      node.value = Math.max(
        outgoing.reduce((sum, l) => sum + l.value, 0),
        inSum,
      );
      return node;
    });

    return {
      enhancedNodes,
      enhancedLinks,
    };
  }, [nodes, links]);

  const maxLevel = Math.max(...enhancedNodes.map((n) => n.level));
  const numColumns = maxLevel;

  // Dynamically calculate leftMargin and rightMargin based on label widths
  const { leftMargin, rightMargin } = useMemo(() => {
    // Find nodes in first and last columns
    const firstColNodes = enhancedNodes.filter((n) => n.level === 1);
    const lastColNodes = enhancedNodes.filter((n) => n.level === maxLevel);
    // Use the maximum possible node height for margin calculation
    const availableHeight = height - 2 * verticalMargin;
    const maxLeft = getMaxLabelWidthForNodes(firstColNodes, availableHeight, maxLevel);
    const maxRight = getMaxLabelWidthForNodes(lastColNodes, availableHeight, maxLevel);
    return {
      leftMargin: Math.ceil(maxLeft + labelPadding + horizontalMargin),
      rightMargin: Math.ceil(maxRight + labelPadding + horizontalMargin),
    };
  }, [enhancedNodes, maxLevel, height, verticalMargin, labelPadding, horizontalMargin]);

  // Compute nodeWidth and columnSpacing and horizontalScale
  const { nodeWidth, columnSpacing, horizontalScale, svgWidth } = useMemo(() => {
    const availableWidth = width - leftMargin - rightMargin;
    const compressedWidth =
      leftMargin + (numColumns - 1) * minLinkWidth + numColumns * minNodeWidth + rightMargin;
    let nodeWidth: number;
    let columnSpacing: number;
    let horizontalScale = 1;
    // let naturalWidth: number; // removed unused variable
    let svgWidth: number;

    if (width >= compressedWidth) {
      // Flexible scaling
      // Node width only reaches maxNodeWidth if width >= 1000
      let effectiveMaxNodeWidth =
        width >= 1000
          ? maxNodeWidth
          : minNodeWidth +
            (maxNodeWidth - minNodeWidth) *
              Math.max(0, Math.min(1, (width - minDiagramWidth) / (1000 - minDiagramWidth)));
      effectiveMaxNodeWidth = Math.max(minNodeWidth, Math.min(maxNodeWidth, effectiveMaxNodeWidth));
      const computedNodeWidth = (availableWidth - (numColumns - 1) * minLinkWidth) / numColumns;
      nodeWidth = Math.max(minNodeWidth, Math.min(effectiveMaxNodeWidth, computedNodeWidth));
      columnSpacing = (availableWidth - numColumns * nodeWidth) / (numColumns - 1);
      columnSpacing = Math.max(minLinkWidth, columnSpacing);
      // naturalWidth = leftMargin + (numColumns - 1) * columnSpacing + nodeWidth * numColumns + rightMargin;
      svgWidth = width;
      // If the natural width (with current nodeWidth and columnSpacing) is greater than the container width, scale links horizontally
      const currentNaturalWidth =
        leftMargin + (numColumns - 1) * columnSpacing + nodeWidth * numColumns + rightMargin;
      if (currentNaturalWidth > width) {
        const linkSpan = (numColumns - 1) * columnSpacing;
        horizontalScale = (width - leftMargin - rightMargin - nodeWidth * numColumns) / linkSpan;
        if (horizontalScale < 0) horizontalScale = 0;
      }
    } else {
      // Compressed mode: use minNodeWidth and minLinkWidth, no scaling, allow scroll
      nodeWidth = minNodeWidth;
      columnSpacing = minLinkWidth;
      horizontalScale = 1;
      // naturalWidth = compressedWidth;
      svgWidth = compressedWidth;
    }
    return { nodeWidth, columnSpacing, horizontalScale, svgWidth };
  }, [
    width,
    numColumns,
    leftMargin,
    rightMargin,
    minLinkWidth,
    minNodeWidth,
    maxNodeWidth,
    minDiagramWidth,
  ]);

  // Compute levels (columns)
  const levels = useMemo(() => {
    const result: SankeyNodeWithLinks[][] = [];
    for (let levelIndex = 0; levelIndex < maxLevel; levelIndex++) {
      if (levelIndex === 0) {
        result.push(
          enhancedNodes.filter((n) => n.level === levelIndex + 1).sort((a, b) => b.value - a.value),
        );
      } else {
        const prevLevelNodes = result[levelIndex - 1];
        const thisLevelNodes = enhancedNodes.filter((n) => n.level === levelIndex + 1);
        const parentToChildren: Record<string, { node: SankeyNodeWithLinks; value: number }[]> = {};
        thisLevelNodes.forEach((node) => {
          if (node.sourceLink) {
            const { sourceNode, value } = node.sourceLink;
            if (!parentToChildren[sourceNode.id]) {
              parentToChildren[sourceNode.id] = [];
            }
            parentToChildren[sourceNode.id].push({ node, value });
          }
        });
        let ordered: SankeyNodeWithLinks[] = [];
        prevLevelNodes.forEach((parent) => {
          const children = parentToChildren[parent.id] || [];
          children.sort((a, b) => b.value - a.value);
          ordered = ordered.concat(children.map((c) => c.node));
        });
        const orphans = thisLevelNodes.filter((n) => !ordered.includes(n));
        ordered = ordered.concat(orphans);
        // Move terminating nodes to the end
        const nonTerminating = ordered.filter((n) => (n.targetLinks?.length ?? 0) > 0);
        const terminating = ordered.filter((n) => (n.targetLinks?.length ?? 0) === 0);
        result.push([...nonTerminating, ...terminating]);
      }
    }
    return result;
  }, [enhancedNodes, maxLevel]);

  // Precompute sorted outgoing links for each node
  const nodeOutgoingLinks = useMemo(() => {
    const terminalChannelNodeIds = new Set(
      enhancedNodes
        .filter((n) => n.level === 2 && (!n.targetLinks || n.targetLinks.length === 0))
        .map((n) => n.id),
    );
    const map: Record<string, SankeyLinkWithNodes[]> = {};
    enhancedNodes.forEach((node) => {
      if (node.level === 1) {
        const outgoing = node.targetLinks || [];
        const toTerminal = outgoing.filter((l) => terminalChannelNodeIds.has(l.targetNode.id));
        const toNonTerminal = outgoing.filter((l) => !terminalChannelNodeIds.has(l.targetNode.id));
        map[node.id] = [
          ...toNonTerminal.sort((a, b) => b.value - a.value),
          ...toTerminal.sort((a, b) => b.value - a.value),
        ];
      } else {
        map[node.id] = (node.targetLinks || []).sort((a, b) => b.value - a.value);
      }
    });
    return map;
  }, [enhancedNodes]);

  // Calculate node positions and heights and link geometry
  useMemo(() => {
    // Step 1: For each column, calculate sum of node values and number of nodes
    const columnStats = levels.map((levelNodes) => {
      const sumValues = levelNodes.reduce((sum, node) => sum + node.value, 0);
      const numNodes = levelNodes.length;
      return { sumValues, numNodes };
    });
    // Step 2: For each column, calculate the scale factor that would fit that column
    const availableHeight = height - 2 * verticalMargin;
    const columnScales = columnStats.map(({ sumValues, numNodes }) => {
      // If no nodes, avoid division by zero
      if (sumValues === 0) return Infinity;
      return (availableHeight - (numNodes - 1) * nodeVerticalPadding) / sumValues;
    });
    // Step 3: Use the minimum scale factor across all columns
    const scaleFactor = Math.min(...columnScales);
    // Step 4: Place nodes
    levels.forEach((levelNodes, colIndex) => {
      // Compute total height for this column (with new scale)
      const totalNodeHeight = levelNodes.reduce((sum, node) => sum + node.value * scaleFactor, 0);
      const totalPadding = (levelNodes.length - 1) * nodeVerticalPadding;
      const totalHeight = totalNodeHeight + totalPadding;
      // Center vertically
      let yOffset = verticalMargin + (availableHeight - totalHeight) / 2;
      levelNodes.forEach((node, i) => {
        const nodeHeight = node.value * scaleFactor;
        node.x = leftMargin + colIndex * (columnSpacing + nodeWidth);
        node.y = yOffset;
        node.width = nodeWidth;
        node.height = nodeHeight;
        yOffset += nodeHeight;
        if (i < levelNodes.length - 1) {
          yOffset += nodeVerticalPadding;
        }
      });
    });
    // Precompute link geometry using sorted outgoing links
    const linkTargetOffset = new Map<string, number>();
    enhancedNodes.forEach((node) => {
      const outgoing = nodeOutgoingLinks[node.id] || [];
      let sourceOffset = 0;
      outgoing.forEach((link) => {
        const sourceNode = link.sourceNode;
        const targetNode = link.targetNode;
        const sourceTotal = (sourceNode.targetLinks || []).reduce((sum, l) => sum + l.value, 0);
        const targetTotal = (targetNode.targetLinks || []).reduce((sum, l) => sum + l.value, 0);
        const sourceScale = sourceNode.height / sourceTotal;
        const targetScale = targetNode.height / targetTotal;
        const linkThickness = link.value * Math.min(sourceScale, targetScale);
        const targetOffset = linkTargetOffset.get(targetNode.id) || 0;
        // Adjust x0 and x1 for horizontalScale
        const x0 = sourceNode.x + sourceNode.width;
        const x1 = targetNode.x;
        const baseLinkSpan = x1 - x0;
        const scaledLinkSpan =
          baseLinkSpan * horizontalScale + baseLinkSpan * (1 - horizontalScale);
        link.x0 = x0;
        link.y0 = sourceNode.y + sourceOffset + linkThickness / 2;
        link.x1 = x0 + scaledLinkSpan;
        link.y1 = targetNode.y + targetOffset + linkThickness / 2;
        link.thickness = linkThickness;
        sourceOffset += linkThickness;
        linkTargetOffset.set(targetNode.id, targetOffset + linkThickness);
      });
    });
    // Sort sourceLinks and targetLinks for each node by vertical position
    enhancedNodes.forEach((node) => {
      if (node.targetLinks) {
        node.targetLinks.sort((a, b) => a.targetNode.y - b.targetNode.y);
      }
    });
  }, [
    levels,
    enhancedNodes,
    height,
    nodeOutgoingLinks,
    nodeWidth,
    columnSpacing,
    horizontalScale,
    leftMargin,
    verticalMargin,
    nodeVerticalPadding,
  ]);

  useEffect(() => {
    if (enhancedNodes.length > 0 && focusedNodeId === null) {
      setFocusedNodeId(enhancedNodes[0].id);
    }
  }, [enhancedNodes, focusedNodeId]);

  const getNodeIndexById = (id: string) => enhancedNodes.findIndex((n) => n.id === id);

  const handleNodeFocus = (nodeId: string) => {
    const idx = getNodeIndexById(nodeId);
    if (idx === -1) {
      return;
    }

    const nextNode = enhancedNodes[idx];
    setFocusedNodeId(nextNode.id);
    setHoveredNode(nextNode); // this will also set the tooltip to open

    const ref = nodeRefs.current[nextNode.id];
    if (ref) {
      ref.focus();
    }
  };

  // Keyboard navigation handler
  const handleNodeKeyDown = (e: KeyboardEvent<SVGGElement>, nodeId: string) => {
    const idx = getNodeIndexById(nodeId);
    if (idx === -1) return;
    let nextIdx = idx;
    if (e.key === 'Home') {
      e.preventDefault();
      nextIdx = 0;
      handleNodeFocus(enhancedNodes[nextIdx].id);
      return;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIdx = enhancedNodes.length - 1;
      handleNodeFocus(enhancedNodes[nextIdx].id);
      return;
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIdx = (idx + 1) % enhancedNodes.length;
      e.preventDefault();
      handleNodeFocus(enhancedNodes[nextIdx].id);
      return;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIdx = (idx - 1 + enhancedNodes.length) % enhancedNodes.length;
      e.preventDefault();
      handleNodeFocus(enhancedNodes[nextIdx].id);
      return;
    }
    // Keyboard shortcut for selecting a node (Enter/Space)
    if ((e.key === 'Enter' || e.key === ' ') && typeof nodeSelectHandler === 'function') {
      e.preventDefault();
      nodeSelectHandler(e, enhancedNodes[idx]);
    }
  };

  // Helper to call the onSelect logic for a node
  const nodeSelectHandler = (
    e: MouseEvent<SVGElement> | KeyboardEvent<SVGElement>,
    node: SankeyNodeWithLinks,
  ) => {
    if (didClickOnStickyPopover(e.target as Element)) {
      return;
    }
    if (stickyNode?.node.id === node.id) {
      setStickyNode(undefined);
    } else {
      setStickyNode({
        element: e.currentTarget as SVGElement as SVGRectElement,
        node,
      });
    }
  };

  return (
    <svg width={svgWidth} height={height} aria-label={description}>
      <defs>
        <filter id={id} x="-100%" y="-100%" width="300%" height="300%">
          <feFlood result="flood" floodColor="white" floodOpacity="1" />
          <feComposite in="flood" in2="SourceAlpha" operator="in" result="mask" />
          <feGaussianBlur in="mask" stdDeviation="4" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {enhancedLinks.map((link) => (
        <SimpleSankeyLink
          key={`${link.sourceNode.id}-${link.targetNode.id}`}
          link={link}
          hoveredNode={hoveredNode}
          onMouseEnter={() => setHoveredNode(link.targetNode)}
          onMouseExit={() => setHoveredNode(undefined)}
          horizontalScale={horizontalScale}
        />
      ))}
      <g role="listbox">
        {enhancedNodes.map((node) => (
          <SimpleSankeyNode
            key={node.id}
            ref={(el) => {
              nodeRefs.current[node.id] = el;
            }}
            node={node}
            maxLevel={maxLevel}
            textFilterId={id}
            hoveredNode={hoveredNode}
            isTooltipOpen={hoveredNode?.id === node.id || stickyNode?.node.id === node.id}
            isTooltipSticky={stickyNode?.node.id === node.id}
            minHitboxHeight={minHitboxHeight}
            labelHorizontalPadding={labelHorizontalPadding}
            tabIndex={focusedNodeId === node.id ? 0 : -1}
            onStickyTooltipClose={() => setStickyNode(undefined)}
            onMouseEnter={() => setHoveredNode(node)}
            onMouseExit={() => setHoveredNode(undefined)}
            onSelect={(e) => nodeSelectHandler(e, node)}
            onFocus={() => setFocusedNodeId(node.id)}
            onKeyDown={(e) => handleNodeKeyDown(e, node.id)}
          />
        ))}
      </g>
    </svg>
  );
};

export default SimpleSankeyDiagram;
